import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton, Stack, Typography } from '@mui/material'
import { EditPencilIcon } from 'Icons/Edit'
import { TrashIcon } from 'Icons/Trash'
import { ConfirmationDialog } from 'Components/Dialog'
import { SectionLoader } from 'Components/Section'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { URL_KEYS } from 'Constants/Url'
import { IKoreanUniversity } from 'Entities/koreanUniversity'
import { useKoreanLanguage2 } from '../Hooks/useKoreanLanguageForm'
import { useKoreanUniversities } from '../Hooks/useKoreanUniversities'
import { KoreanUniversityEditDialog } from './KoreanUniversityEditDialog'

const getUniversityTitle = (item: IKoreanUniversity, lang?: string | null) => {
  switch (lang) {
    case 'Uz':
      return item.universityNameUz
    case 'Kr':
      return item.universityNameKo
    case 'Ru':
    default:
      return item.universityNameRu
  }
}

export const KoreanUniversityList = () => {
  const { regions, isRegionsLoading, isRegionsFetching } = useKoreanLanguage2()
  const {
    universities,
    isUniversitiesLoading,
    isUniversitiesFetching,
    deleteUniversityMutation,
  } = useKoreanUniversities()

  const [searchParams] = useSearchParams()
  const lang = searchParams.get(URL_KEYS.LANG)

  const [expandedRegion, setExpandedRegion] = useState<number | null>(null)
  const [removeUniversityId, setRemoveUniversityId] = useState<number | null>(null)
  const [editUniversity, setEditUniversity] = useState<IKoreanUniversity | null>(null)

  const regionUniversities = useMemo(() => {
    if (expandedRegion == null) return []
    return universities.filter(u => u.regionCode === expandedRegion)
  }, [expandedRegion, universities])

  const handleAccordionChange = useCallback((regionCode: number) => {
    setExpandedRegion(prev => (prev === regionCode ? null : regionCode))
  }, [])

  const onRemove = useCallback(() => {
    if (removeUniversityId == null) return
    deleteUniversityMutation.mutate(removeUniversityId, {
      onSuccess: () => {
        setRemoveUniversityId(null)
      },
    })
  }, [deleteUniversityMutation, removeUniversityId])

  return (
    <>
      <ConfirmationDialog open={removeUniversityId != null} onAccept={onRemove} onClose={() => setRemoveUniversityId(null)} />

      {editUniversity && (
        <KoreanUniversityEditDialog
          university={editUniversity}
          open={!!editUniversity}
          onClose={() => setEditUniversity(null)}
          regionCode={editUniversity.regionCode}
        />
      )}

      <SectionLoader isLoading={isRegionsLoading} isFetching={isRegionsFetching}>
        <Grid container rowSpacing={2}>
          {regions.map((region: { regionCode: number; regionName: string }) => {
            const isExpanded = expandedRegion === region.regionCode

            return (
              <Grid item xs={12} key={region.regionCode}>
                <Accordion expanded={isExpanded} onChange={() => handleAccordionChange(region.regionCode)}>
                  <AccordionSummary>
                    <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%' pr={2}>
                      <Typography fontWeight={700}>{region.regionName}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        지역 코드: {region.regionCode}
                      </Typography>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    {isExpanded && (
                      <SectionLoader isLoading={isUniversitiesLoading} isFetching={isUniversitiesFetching}>
                        <Stack spacing={1.5}>
                          {regionUniversities.length ? (
                            regionUniversities.map(item => (
                              <Box
                                key={item.universityId}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1.5,
                                }}>
                                <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
                                  <Box>
                                    <Typography fontWeight={600}>{getUniversityTitle(item, lang)}</Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                      {item.typeName} · ID: {item.universityId}
                                    </Typography>
                                  </Box>

                                  <Stack direction='row' spacing={1}>
                                    <IconButton onClick={() => setEditUniversity(item)}>
                                      <EditPencilIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setRemoveUniversityId(item.universityId)}>
                                      <TrashIcon />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              </Box>
                            ))
                          ) : (
                            <Typography color='text.secondary'>등록된 대학이 없습니다.</Typography>
                          )}
                        </Stack>
                      </SectionLoader>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )
          })}
        </Grid>
      </SectionLoader>
    </>
  )
}
